import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OverviewTab }    from "../components/analytics/OverviewTab";
import { TimeTab }        from "../components/analytics/TimeTab";
import { ProductsTab }    from "../components/analytics/ProductsTab";
import { TeamTablesTab }  from "../components/analytics/TeamTablesTab";
import { ForecastTab }    from "../components/analytics/ForecastTab";
import { AITab }          from "../components/analytics/AITab";
import { MultiStoreTab }  from "../components/analytics/MultiStoreTab";
import { TicketsPanel }   from "../components/dashboard/TicketsPanel";
import {
  TrendingUp, Clock, Package, Users, Receipt, Sparkles, Store, BarChart2,
  ChevronLeft, CalendarDays
} from "lucide-react";
import api from "../lib/api";

// ── Date helpers ────────────────────────────────────────────────────────────
function getPresetDates(preset) {
  const now = new Date();
  const fmt = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,"0"), day = String(d.getDate()).padStart(2,"0");
    return `${y}-${m}-${day}`;
  };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today":     return { start: fmt(today), end: fmt(today) };
    case "week":      return { start: fmt(new Date(today.getTime() - 6*86400000)), end: fmt(today) };
    case "month":     return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
    case "quarter":   return { start: fmt(new Date(today.getFullYear(), Math.floor(today.getMonth()/3)*3, 1)), end: fmt(today) };
    case "year":      return { start: fmt(new Date(today.getFullYear(), 0, 1)), end: fmt(today) };
    case "year_prev": return { start: fmt(new Date(today.getFullYear()-1, 0, 1)), end: fmt(new Date(today.getFullYear()-1, 11, 31)) };
    default:          return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
  }
}

// ── Tabs definition ─────────────────────────────────────────────────────────
const TABS = [
  { id: "overview",    label: "Resumen",       icon: BarChart2 },
  { id: "time",        label: "Tiempo",        icon: Clock },
  { id: "products",    label: "Productos",     icon: Package },
  { id: "team-tables", label: "Equipo & Mesas",icon: Users },
  { id: "tickets",     label: "Tickets",       icon: Receipt },
  { id: "forecast",    label: "Predicciones",  icon: TrendingUp },
  { id: "ai",          label: "IA Asistente",  icon: Sparkles },
];

// ── Export helpers ──────────────────────────────────────────────────────────
function triggerPrint() { window.print(); }

// ── Main component ──────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const { currentRestaurant, restaurants } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialView = searchParams.get("view") || "overview";
  const initialRange = searchParams.get("range") || "month";
  const [activeTab, setActiveTab]       = useState(initialView);
  const [datePreset, setDatePreset]     = useState(initialRange);
  const [customStart, setCustomStart]   = useState("");
  const [customEnd, setCustomEnd]       = useState("");
  const [isCustom, setIsCustom]         = useState(false);

  // Multi-store visibility: show only when user has >1 restaurant
  const hasMultiStore = restaurants && restaurants.length > 1;
  const allTabs = hasMultiStore ? [...TABS, { id: "multi-store", label: "Multi-tienda", icon: Store }] : TABS;

  // Sync tab with URL param
  const switchTab = (id) => {
    setActiveTab(id);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set("view", id); return p; }, { replace: true });
  };

  useEffect(() => {
    const v = searchParams.get("view");
    if (v) setActiveTab(v);
  }, [searchParams]);

  const { start, end } = isCustom && customStart && customEnd
    ? { start: customStart, end: customEnd }
    : getPresetDates(datePreset);

  const restaurantId = currentRestaurant?.restaurant_id;
  const closedDays = currentRestaurant?.closed_days || [];

  const renderTab = () => {
    const props = { restaurantId, startDate: start, endDate: end };
    switch (activeTab) {
      case "overview":    return <OverviewTab {...props} onNavigate={switchTab} />;
      case "time":        return <TimeTab {...props} closedDays={closedDays} />;
      case "products":    return <ProductsTab {...props} />;
      case "team-tables": return <TeamTablesTab {...props} />;
      case "tickets":     return <TicketsPanel restaurantId={restaurantId} startDate={start} endDate={end} />;
      case "forecast":    return <ForecastTab restaurantId={restaurantId} />;
      case "ai":          return <AITab restaurantId={restaurantId} startDate={start} endDate={end} />;
      case "multi-store": return <MultiStoreTab startDate={start} endDate={end} />;
      default:            return <OverviewTab {...props} onNavigate={switchTab} />;
    }
  };

  return (
    <div className="space-y-6 print:space-y-4">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4 print:hidden">
        <div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/app")} className="gap-1 text-xs text-muted-foreground h-7 px-2">
              <ChevronLeft className="w-3 h-3" />Dashboard
            </Button>
          </div>
          <h1 className="text-2xl font-bold mt-1">Análisis de ventas</h1>
          {currentRestaurant && (
            <p className="text-sm text-muted-foreground">{currentRestaurant.name}</p>
          )}
        </div>

        {/* Date controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-0.5">
            {[
              { v: "today",     l: "Hoy" },
              { v: "week",      l: "7 días" },
              { v: "month",     l: "Mes" },
              { v: "quarter",   l: "Trimestre" },
              { v: "year",      l: "Año" },
              { v: "year_prev", l: "Año ant." },
            ].map(({ v, l }) => (
              <button key={v} onClick={() => { setDatePreset(v); setIsCustom(false); }}
                data-testid={`analytics-preset-${v}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${!isCustom && datePreset === v ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
            <button onClick={() => setIsCustom(true)}
              data-testid="analytics-preset-custom"
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${isCustom ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Personalizado
            </button>
          </div>

          {isCustom && (
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                className="h-8 px-2 rounded-md border border-input bg-background text-xs"
                data-testid="analytics-custom-start" />
              <span className="text-muted-foreground text-xs">—</span>
              <input type="date" value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                className="h-8 px-2 rounded-md border border-input bg-background text-xs"
                data-testid="analytics-custom-end" />
            </div>
          )}

          <Button variant="ghost" size="sm" onClick={triggerPrint} className="gap-1 text-xs h-8 print:hidden" data-testid="print-analytics">
            <CalendarDays className="w-3.5 h-3.5" />Imprimir
          </Button>
        </div>
      </div>

      {/* ── Tab nav ────────────────────────────────────────────────── */}
      <div className="border-b border-border print:hidden">
        <div className="flex overflow-x-auto gap-1 pb-0 no-scrollbar">
          {allTabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => switchTab(tab.id)}
                data-testid={`analytics-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${active
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {tab.label}
                {tab.id === "multi-store" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">{restaurants?.length}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Period indicator ───────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground print:block">
        <CalendarDays className="w-3.5 h-3.5 print:hidden" />
        <span>Período: <strong>{start}</strong> – <strong>{end}</strong></span>
        {currentRestaurant && <span className="print:inline hidden">· {currentRestaurant.name}</span>}
      </div>

      {/* ── Tab content ────────────────────────────────────────────── */}
      <div data-testid={`analytics-content-${activeTab}`}>
        {renderTab()}
      </div>
    </div>
  );
}
